import { useMemo } from 'react';

import { MDText } from 'i18n-react';

import { Slug } from 'Utilities/profiling/@types';
import { isAbleToSeeOffers, isStatusTimeline } from 'Utilities/restricted';
import { EnquiryOfferResponse } from 'Utilities/vehicles/hooks/useGetEnquiryOffers';

import { PAYMENT_STATUS_ROUTE_NAMES } from '../../../documentCapture/DocumentCapture.helpers';
import { LatestPostSaleOffer, PaymentStatuses } from '../../../payments/Payments.types';
import { PROFILING_PATH } from '../../../vehicleProfiling/VehicleProfiling.constants';
import { generateVehicleCardActions } from '../../Pages/AccountPage/AccountPage.helpers';
import AccountTexts from '../../Pages/AccountPage/AccountPage.texts.json';

import { getPaymentCta } from './useVehicleCard.helpers';

const AccountT = new MDText(AccountTexts);

export type UseVehicleCardCtaProps = {
  dpEnquiryId?: number | null;
  enquiryOffers?: EnquiryOfferResponse | null;
  enquiryStateSlug?: Slug | null;
  isBelowOriginalReservePrice?: boolean;
  isCVT?: boolean;
  isEnquiryExpired: boolean;
  latestPostSaleOfferData?: LatestPostSaleOffer | null;
  paymentsStatuses?: PaymentStatuses | null;
  vrm?: string;
};

export const useVehicleCardCta = ({
  dpEnquiryId,
  enquiryOffers,
  enquiryStateSlug,
  isBelowOriginalReservePrice,
  isCVT,
  isEnquiryExpired,
  latestPostSaleOfferData,
  paymentsStatuses,
  vrm,
}: UseVehicleCardCtaProps): { gaEvent: string; label: string; link: string } => {
  const { gaEvent, label: primaryCtaLabel, path } = generateVehicleCardActions(
    enquiryStateSlug,
    dpEnquiryId,
  );

  const isPaymentLink = paymentsStatuses || latestPostSaleOfferData?.type === 'counter_offer';
  const canSeeOffers = (enquiryOffers && isAbleToSeeOffers(enquiryOffers.state?.slug)) || isBelowOriginalReservePrice;

  const link = useMemo((): string => {
    if (isPaymentLink) {
      return `/${vrm}/${PAYMENT_STATUS_ROUTE_NAMES.VEHICLE_PAYMENT}`;
    }

    let targetPath = path;

    if (canSeeOffers) {
      targetPath = '/accept-offer';
    } else if (isStatusTimeline(enquiryStateSlug)) {
      targetPath = `/profile${PROFILING_PATH.TIMELINE}`;
    }

    return `/${vrm}${targetPath}`;
  }, [isPaymentLink, vrm, path, canSeeOffers, enquiryStateSlug]);

  const label = useMemo(() => {
    const paymentsCTA = getPaymentCta(paymentsStatuses, latestPostSaleOfferData);

    if (paymentsCTA) {
      return paymentsCTA;
    }

    if (canSeeOffers) {
      return AccountT.translate('seeOffersLabel');
    }

    if (isEnquiryExpired) {
      return isCVT ? AccountT.translate('expiredEnquiryCVT') : AccountT.translate('expiredEnquiry');
    }

    return primaryCtaLabel;
  }, [
    isCVT,
    canSeeOffers,
    latestPostSaleOfferData,
    paymentsStatuses,
    primaryCtaLabel,
    isEnquiryExpired,
  ]);

  return { gaEvent, label, link };
};
